import React from "react";
import { ASSETS } from "./assets";

// Reusable phone frame that renders an in-frame screen for a Rallie app screenshot.
// The screen content is passed as children (SVG/HTML) so we can mock different screens.
export default function PhoneMockup({ children, className = "", tilt = 0, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative w-full h-full ${className}`}
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      <div className="relative w-full h-full rounded-[2.5rem] bg-neutral-950 border border-neutral-800 shadow-[0_40px_80px_-20px_rgba(212,243,74,0.15)] p-2 overflow-hidden">
        {/* notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-10" />
        {/* screen */}
        <div className="relative w-full h-full rounded-[2rem] bg-black overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ---------- Real screenshot presets (from customer assets) ---------- */

const RealScreen = ({ src, alt, scale = 1.55, offsetY = 0 }) => (
  <div className="w-full h-full overflow-hidden bg-black">
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover object-center"
      style={{
        transform: `scale(${scale}) translateY(${offsetY}px)`,
        transformOrigin: "center center",
      }}
    />
  </div>
);

export function RealScreenHome() {
  return (
    <RealScreen
      src={ASSETS.screenHomeZoom}
      alt="Rallie home screen — Your match is live"
      scale={0.98}
      offsetY={8}
    />
  );
}

export function RealScreenPodium() {
  return (
    <RealScreen
      src={ASSETS.screenPodium}
      alt="Rallie tournament podium with confetti"
      scale={1.15}
    />
  );
}

export function RealScreenSignIn() {
  return (
    <RealScreen
      src={ASSETS.screenSignIn}
      alt="Rallie sign in screen"
      scale={1.1}
    />
  );
}

export function RealScreenDevice() {
  // The "device-in-canvas" shot — useful as a decorative element (e.g. features bg)
  return (
    <RealScreen
      src={ASSETS.screenHomeSmall}
      alt="Rallie app on phone"
      scale={1.0}
    />
  );
}

/* ---------- Screen content presets ---------- */

export function ScreenHome() {
  return (
    <div className="w-full h-full bg-black text-white p-4 pt-10 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase text-neutral-500 tracking-widest">
            Hey, Marco
          </div>
          <div className="font-display text-lg uppercase leading-none">
            Ready to play?
          </div>
        </div>
        <div className="w-8 h-8 rounded-full bg-[#D4F34A]" />
      </div>

      <div className="rounded-2xl border border-[#D4F34A]/40 bg-[#D4F34A]/5 p-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#D4F34A] animate-pulse" />
          <span className="text-[10px] uppercase tracking-widest text-[#D4F34A]">
            Live
          </span>
        </div>
        <div className="font-display text-base uppercase mt-1 leading-tight">
          Sunday Americano
        </div>
        <div className="text-[10px] text-neutral-400 mt-1">
          Round 3 of 6 · 12 players
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-xl border border-neutral-800 p-2">
          <div className="text-[9px] uppercase text-neutral-500">Matches</div>
          <div className="font-display text-lg">128</div>
        </div>
        <div className="rounded-xl border border-neutral-800 p-2">
          <div className="text-[9px] uppercase text-neutral-500">Win rate</div>
          <div className="font-display text-lg text-[#D4F34A]">64%</div>
        </div>
      </div>

      <div className="mt-auto space-y-2">
        <div className="rounded-xl bg-white text-black p-3 flex items-center justify-between">
          <span className="font-display uppercase text-sm">New Tournament</span>
          <span className="text-lg">+</span>
        </div>
        <div className="rounded-xl border border-neutral-800 p-3 flex items-center justify-between">
          <span className="font-display uppercase text-sm">Quick Match</span>
          <span className="text-neutral-500 text-lg">›</span>
        </div>
      </div>
    </div>
  );
}

export function ScreenLiveScore() {
  return (
    <div className="w-full h-full bg-black text-white p-4 pt-10 flex flex-col">
      <div className="text-center mb-2">
        <div className="text-[9px] uppercase tracking-widest text-neutral-500">
          Set 2 · Game 5
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#D4F34A] mt-1">
          Live
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center gap-3">
        <div className="rounded-2xl border border-neutral-800 p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase text-neutral-500">Team A</div>
              <div className="font-display text-sm uppercase leading-tight">
                M. Rossi · L. Vale
              </div>
            </div>
            <div className="font-display text-4xl text-[#D4F34A] leading-none">
              40
            </div>
          </div>
          <div className="flex gap-1 mt-2">
            <span className="text-[9px] uppercase text-neutral-500">Sets</span>
            <span className="font-display text-xs">1</span>
            <span className="font-display text-xs text-neutral-500">·</span>
            <span className="font-display text-xs">4</span>
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-800 p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase text-neutral-500">Team B</div>
              <div className="font-display text-sm uppercase leading-tight">
                A. Silva · J. Cruz
              </div>
            </div>
            <div className="font-display text-4xl leading-none">30</div>
          </div>
          <div className="flex gap-1 mt-2">
            <span className="text-[9px] uppercase text-neutral-500">Sets</span>
            <span className="font-display text-xs">0</span>
            <span className="font-display text-xs text-neutral-500">·</span>
            <span className="font-display text-xs">3</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="rounded-xl bg-[#D4F34A] text-black py-3 text-center font-display uppercase text-sm">
          + Team A
        </div>
        <div className="rounded-xl border border-neutral-800 py-3 text-center font-display uppercase text-sm">
          + Team B
        </div>
      </div>
    </div>
  );
}

export function ScreenPodium() {
  return (
    <div className="w-full h-full bg-black text-white p-4 pt-10 flex flex-col items-center">
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">
        Tournament complete
      </div>
      <div className="font-display uppercase text-lg mt-1">Podium</div>

      <div className="flex items-end gap-2 mt-6 w-full justify-center">
        <div className="flex flex-col items-center w-20">
          <div className="text-[9px] uppercase text-neutral-500">L. Vale</div>
          <div className="w-full h-16 bg-neutral-800 rounded-t-md flex items-center justify-center font-display text-2xl">
            2
          </div>
        </div>
        <div className="flex flex-col items-center w-24">
          <div className="text-[9px] uppercase text-[#D4F34A]">M. Rossi</div>
          <div className="w-full h-24 bg-[#D4F34A] text-black rounded-t-md flex items-center justify-center font-display text-3xl">
            1
          </div>
        </div>
        <div className="flex flex-col items-center w-20">
          <div className="text-[9px] uppercase text-neutral-500">A. Silva</div>
          <div className="w-full h-12 bg-neutral-800 rounded-t-md flex items-center justify-center font-display text-xl">
            3
          </div>
        </div>
      </div>

      <div className="mt-6 w-full space-y-1">
        {[
          ["4", "J. Cruz", "18"],
          ["5", "F. Gomez", "16"],
          ["6", "T. Nardi", "14"],
        ].map(([r, name, pts]) => (
          <div
            key={r}
            className="flex items-center justify-between text-[11px] px-2 py-1 border-b border-neutral-900"
          >
            <div className="flex items-center gap-2">
              <span className="text-neutral-500 font-display">{r}</span>
              <span>{name}</span>
            </div>
            <span className="font-display">{pts}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScreenPlayers() {
  return (
    <div className="w-full h-full bg-black text-white p-4 pt-10">
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">
        Community
      </div>
      <div className="font-display uppercase text-lg mb-3">Players</div>

      <div className="rounded-xl border border-neutral-800 p-2 mb-3 text-[11px] text-neutral-400">
        Search 12,481 players…
      </div>

      {[
        ["MR", "Marco Rossi", "1240", true],
        ["LV", "Luca Vale", "1180", true],
        ["AS", "Ana Silva", "1102", false],
        ["JC", "Jorge Cruz", "980", false],
        ["FG", "Fer Gomez", "912", false],
      ].map(([initials, name, pts, added]) => (
        <div
          key={name}
          className="flex items-center justify-between py-2 border-b border-neutral-900"
        >
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-neutral-800 flex items-center justify-center text-[10px] font-bold">
              {initials}
            </div>
            <div>
              <div className="text-[12px]">{name}</div>
              <div className="text-[9px] text-neutral-500">{pts} pts</div>
            </div>
          </div>
          <div
            className={`text-[9px] uppercase tracking-widest px-2 py-1 rounded-full ${
              added
                ? "bg-[#D4F34A] text-black"
                : "border border-neutral-700 text-neutral-400"
            }`}
          >
            {added ? "Added" : "Add"}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ScreenStats() {
  return (
    <div className="w-full h-full bg-black text-white p-4 pt-10">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-[#D4F34A] flex items-center justify-center text-black font-display text-xl">
          MR
        </div>
        <div>
          <div className="font-display uppercase text-base leading-none">
            Marco Rossi
          </div>
          <div className="text-[10px] text-neutral-500">Rank #12 · Milan</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          ["128", "Matches"],
          ["82", "Wins"],
          ["64%", "Win %"],
        ].map(([n, l]) => (
          <div
            key={l}
            className="rounded-xl border border-neutral-800 p-2 text-center"
          >
            <div className="font-display text-lg">{n}</div>
            <div className="text-[9px] uppercase text-neutral-500 tracking-widest">
              {l}
            </div>
          </div>
        ))}
      </div>

      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
        Last 10 matches
      </div>
      <div className="flex gap-1">
        {["W", "W", "L", "W", "W", "L", "W", "W", "W", "L"].map((r, i) => (
          <div
            key={i}
            className={`flex-1 h-8 rounded-md flex items-center justify-center font-display text-xs ${
              r === "W" ? "bg-[#D4F34A] text-black" : "bg-neutral-800"
            }`}
          >
            {r}
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-xl border border-neutral-800 p-3">
        <div className="text-[10px] uppercase text-neutral-500 tracking-widest">
          Career points
        </div>
        <div className="font-display text-3xl text-[#D4F34A] leading-none mt-1">
          1,240
        </div>
      </div>
    </div>
  );
}

export function ScreenCreateTournament() {
  return (
    <div className="w-full h-full bg-black text-white p-4 pt-10">
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">
        New tournament
      </div>
      <div className="font-display uppercase text-lg mb-3">
        Americano · 12 players
      </div>

      <div className="rounded-xl border border-neutral-800 p-2 mb-2 text-[11px]">
        <div className="text-[9px] uppercase text-neutral-500">Format</div>
        <div className="font-display uppercase">Best of 3 · Golden Point</div>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2 mb-1">
        Players
      </div>
      <div className="flex flex-wrap gap-1">
        {["M. Rossi", "L. Vale", "A. Silva", "J. Cruz", "F. Gomez", "T. Nardi", "+ 6"].map(
          (p, i) => (
            <span
              key={i}
              className={`text-[10px] px-2 py-1 rounded-full border ${
                i === 6
                  ? "border-[#D4F34A] text-[#D4F34A]"
                  : "border-neutral-700 text-neutral-300"
              }`}
            >
              {p}
            </span>
          ),
        )}
      </div>

      <div className="mt-4 rounded-xl bg-[#D4F34A] text-black py-3 text-center font-display uppercase text-sm">
        Start Tournament
      </div>

      <div className="mt-3 rounded-xl border border-neutral-800 py-3 text-center font-display uppercase text-sm text-neutral-400">
        Save draft
      </div>
    </div>
  );
}

export function ScreenLeaderboard() {
  return (
    <div className="w-full h-full bg-black text-white p-4 pt-10">
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">
        Sunday Americano
      </div>
      <div className="font-display uppercase text-lg mb-3">Leaderboard</div>

      {[
        ["1", "M. Rossi", "24", true],
        ["2", "L. Vale", "22", false],
        ["3", "A. Silva", "20", false],
        ["4", "J. Cruz", "18", false],
        ["5", "F. Gomez", "16", false],
        ["6", "T. Nardi", "14", false],
        ["7", "R. Lopez", "12", false],
      ].map(([r, name, pts, top]) => (
        <div
          key={r}
          className={`flex items-center justify-between py-2 border-b border-neutral-900 ${
            top ? "text-[#D4F34A]" : ""
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="font-display text-sm w-4">{r}</span>
            <span className="text-[12px]">{name}</span>
          </div>
          <span className="font-display text-sm">{pts}</span>
        </div>
      ))}
    </div>
  );
}

export function ScreenAuth() {
  return (
    <div className="w-full h-full bg-black text-white p-6 pt-16 flex flex-col">
      <div className="flex-1 flex flex-col justify-center items-start">
        <div className="w-10 h-10 rounded-full bg-[#D4F34A] mb-4" />
        <div className="font-display uppercase text-3xl leading-none">
          Your padel.
        </div>
        <div className="font-display uppercase text-3xl leading-none">
          Your score.
        </div>
        <div className="font-display uppercase text-3xl leading-none text-[#D4F34A]">
          Your game.
        </div>
        <div className="text-[11px] text-neutral-500 mt-3 leading-snug">
          Sign in to run tournaments, track stats and connect with your padel
          community.
        </div>
      </div>

      <div className="space-y-2">
        <div className="rounded-full bg-white text-black py-3 text-center font-display uppercase text-sm">
          Continue with Apple
        </div>
        <div className="rounded-full bg-transparent border border-neutral-700 py-3 text-center font-display uppercase text-sm">
          Continue with Google
        </div>
        <div className="text-center text-[10px] text-neutral-500 mt-2">
          Sign in with email
        </div>
      </div>
    </div>
  );
}

export function ScreenRegularScore() {
  return (
    <div className="w-full h-full bg-black text-white p-4 pt-10 flex flex-col">
      <div className="text-center text-[9px] uppercase tracking-widest text-neutral-500">
        Regular Match · Set 1
      </div>

      <div className="flex-1 grid grid-cols-2 gap-2 mt-3">
        <div className="rounded-2xl bg-[#D4F34A] text-black p-3 flex flex-col justify-between">
          <div className="text-[10px] uppercase font-bold">Team A</div>
          <div className="font-display text-6xl leading-none">40</div>
          <div className="text-[10px] uppercase">Sets 1</div>
        </div>
        <div className="rounded-2xl border border-neutral-800 p-3 flex flex-col justify-between">
          <div className="text-[10px] uppercase text-neutral-400">Team B</div>
          <div className="font-display text-6xl leading-none">15</div>
          <div className="text-[10px] uppercase text-neutral-400">Sets 0</div>
        </div>
      </div>

      <div className="mt-3 text-center text-[10px] uppercase tracking-widest text-[#D4F34A]">
        Deuce · Golden point
      </div>
    </div>
  );
}
